import * as React from 'react';
import { v4 as uuidv4 } from 'uuid';
import styles from './EditItineraryModal.module.css';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { useMapDisplayContext } from '../../MapDisplayContext';
import { useSpring, animated, config } from '@react-spring/web';
import { useDrag } from '@use-gesture/react';
import { ItineraryPinCard } from '../ItineraryPinCard';
import { useSelector } from 'react-redux';
import { selectDigitalMap } from 'ducks/universal/digitalMap';
import { BackButton } from './BackButton';
import clsx from 'clsx';
import { Itinerary } from 'models/itinerary';
import { generateDateItineraries } from './generateItinerary';
import { DateRangePicker } from 'components/Map/DateRangePicker/DateRangePicker';
import { useIsSmallScreen } from 'components/Map/useIsSmallScreen';

interface EditItineraryModalProps {
  onBack: () => void;
  onCreateClick: (itinerary: Itinerary) => void;
  mode: 'CREATE' | 'EDIT';
}

export const EditItineraryModal: React.FC<EditItineraryModalProps> = ({
  onBack,
  onCreateClick,
  mode,
}) => {
  const { t, i18n } = useTranslation();
  const {
    activeItineraryPinKeys,
    setActiveItineraryPinKeys,
    newItinerary,
  } = useMapDisplayContext();

  const isSmallScreen = useIsSmallScreen();

  let initialStartDate = moment().format('YYYY-MM-DD');
  if ((newItinerary?.date_itineraries?.length || 0) > 0 && newItinerary?.date_itineraries[0].date) {
    initialStartDate = newItinerary?.date_itineraries[0].date;
  }

  let initialEndDate = moment().format('YYYY-MM-DD');
  if (
    (newItinerary?.date_itineraries?.length || 0) > 0 &&
    newItinerary?.date_itineraries[newItinerary?.date_itineraries.length - 1].date
  ) {
    initialEndDate = newItinerary?.date_itineraries[newItinerary?.date_itineraries.length - 1].date;
  }

  const [startDate, setStartDate] = React.useState<string>(initialStartDate);
  const [endDate, setEndDate] = React.useState<string>(initialEndDate);
  const [showStartDatePicker, setShowStartDatePicker] = React.useState(false);
  const [showEndDatePicker, setShowEndDatePicker] = React.useState(false);
  const [position, setPosition] = React.useState<'MINIMIZED' | 'MAXIMIZED'>(
    isSmallScreen ? 'MINIMIZED' : 'MAXIMIZED'
  );

  const digitalMap = useSelector(selectDigitalMap);

  const endDateInputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (mode === 'CREATE') {
      setActiveItineraryPinKeys([]);
    } else {
      setActiveItineraryPinKeys(
        newItinerary?.date_itineraries.flatMap((date) => date.items.map((item) => item.pin_key)) ||
          []
      );
    }
  }, [mode]);

  const [springs] = useSpring(() => ({
    from: { y: 400 },
    to: { y: 0 },
    config: {
      ...config.stiff,
      clamp: true,
    },
    immediate: !isSmallScreen,
  }));

  const bind = useDrag(
    ({ swipe: [, swipeY] }) => {
      if (swipeY < 0 && position === 'MINIMIZED') {
        setPosition('MAXIMIZED');
      } else if (swipeY > 0 && position === 'MAXIMIZED' && isSmallScreen) {
        setPosition('MINIMIZED');
      }
    },
    {
      swipe: {
        distance: [0, 10],
        velocity: [0, 0.1],
        duration: 250,
      },
    }
  );

  const modalStyle: React.CSSProperties = {
    height: position === 'MAXIMIZED' ? 'calc(100dvh - var(--header-height))' : 'auto',
    overflowY: position === 'MAXIMIZED' ? 'auto' : 'visible',
    borderRadius: position === 'MAXIMIZED' ? '0' : '16px 16px 0 0',
  };

  return (
    <div className={styles.container}>
      {position === 'MINIMIZED' && (
        <div className={styles.backButtonContainer}>
          <BackButton onClick={onBack} />
        </div>
      )}
      <div className={styles.modalWrapper}>
        <animated.div
          {...(bind() as any)}
          className={styles.modal}
          style={{
            ...modalStyle,
            ...springs,
          }}
        >
          {position === 'MINIMIZED' && (
            <div
              className={styles.handleContainer}
              onClick={() => {
                setPosition('MAXIMIZED');
              }}
            >
              <div className={styles.handle} />
            </div>
          )}

          <div className={clsx(styles.header, position === 'MAXIMIZED' && styles.maximized)}>
            {position === 'MAXIMIZED' && (
              <div className={styles.maximizedBackButtonContainer}>
                <BackButton onClick={onBack} />
              </div>
            )}
            <h2>{mode === 'CREATE' ? t('Create Itinerary') : t('Edit Details')}</h2>
          </div>

          <div className={styles.content}>
            {activeItineraryPinKeys.length > 0 ? (
              mode === 'CREATE' ? (
                <p className={styles.subtitle}>
                  <strong>{t('{{count}} Pins', { count: activeItineraryPinKeys.length })}</strong>{' '}
                  {t('Added to Your Itinerary.')}
                  <br />
                  {t(' Please Tap To Add Or Remove Pins.')}
                </p>
              ) : (
                <p className={styles.subtitle}>
                  <strong>{t('{{count}} Pins', { count: activeItineraryPinKeys.length })}</strong>{' '}
                  {t('Selected.')}
                </p>
              )
            ) : (
              <p className={styles.subtitle}>
                {i18n.language === 'en-US' ? (
                  <>
                    {t('Please')} <strong>{t('Select Pins')}</strong> {t('You Want To Visit.')}
                  </>
                ) : (
                  <>{t('Please Select Pins That You Want To Visit.')}</>
                )}
              </p>
            )}

            <div className={styles.datePickerContainer}>
              <div
                className={styles.formGroup}
                onClick={() => {
                  setShowStartDatePicker(true);
                }}
              >
                <label className={styles.formLabel}>{t('Start Date')}</label>
                <div className={styles.formControl}>
                  <input
                    type="text"
                    className={styles.formInput}
                    value={moment(startDate).locale(i18n.language).format('LL')}
                    readOnly
                  />
                  <svg
                    className={styles.iconSmall}
                    xmlns="http://www.w3.org/2000/svg"
                    width="8"
                    height="4"
                    viewBox="0 0 8 4"
                    fill="none"
                  >
                    <path
                      d="M1 0.5L4 3.5L7 0.5"
                      stroke="#393D3F"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </div>
              <div
                className={styles.formGroup}
                onClick={() => {
                  setShowEndDatePicker(true);
                }}
              >
                <label className={styles.formLabel}>{t('End Date')}</label>
                <div className={styles.formControl}>
                  <input
                    ref={endDateInputRef}
                    type="text"
                    className={styles.formInput}
                    value={moment(endDate).locale(i18n.language).format('LL')}
                    readOnly
                  />
                  <svg
                    className={styles.iconSmall}
                    xmlns="http://www.w3.org/2000/svg"
                    width="8"
                    height="4"
                    viewBox="0 0 8 4"
                    fill="none"
                  >
                    <path
                      d="M1 0.5L4 3.5L7 0.5"
                      stroke="#393D3F"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </div>
              {(showStartDatePicker || showEndDatePicker) && (
                <DateRangePicker
                  onClose={() => setShowStartDatePicker(false)}
                  onDateChange={(date) => {
                    if (showStartDatePicker) {
                      setStartDate(date);
                      if (endDate < date) {
                        setEndDate(date);
                      }
                      setShowStartDatePicker(false);
                    } else {
                      setEndDate(date);
                      setShowEndDatePicker(false);
                    }
                  }}
                  startDate={startDate}
                  endDate={endDate}
                  minDate={showEndDatePicker ? startDate : undefined}
                />
              )}
            </div>

            <button
              disabled={endDate < startDate || activeItineraryPinKeys.length === 0}
              className={styles.createButton}
              onClick={async () => {
                if (!digitalMap) return;
                onCreateClick({
                  date_itineraries: await generateDateItineraries(
                    digitalMap,
                    startDate,
                    endDate,
                    activeItineraryPinKeys
                  ),
                  digital_map_id: digitalMap.id,
                  title: mode === 'CREATE' ? '' : newItinerary?.title || '',
                  id: mode === 'CREATE' ? uuidv4() : newItinerary?.id || '',
                });
              }}
            >
              {mode === 'CREATE' ? t('Create Itinerary') : t('Update Itinerary')}
            </button>

            {activeItineraryPinKeys.length > 0 && (
              <div
                className={clsx(styles.pinsContainer, position === 'MAXIMIZED' && styles.maximized)}
              >
                {activeItineraryPinKeys.map((pinKey) => {
                  const pin = digitalMap?.pins?.find((pin) => pin.key === pinKey);
                  if (!pin) return null;
                  return (
                    <div key={pinKey} className={styles.pinCard}>
                      <ItineraryPinCard pin={pin} />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </animated.div>
      </div>
    </div>
  );
};
